import React, { useEffect, useState } from "react";
import { getTop, getUsernameAndUrl } from "../api";
import "../index.css";

const DEFAULT_AVATAR =
  "https://static.vecteezy.com/system/resources/thumbnails/003/337/584/small/default-avatar-photo-placeholder-profile-icon-vector.jpg";

/**
 * Puppeteer render target: the /top command records this page for 10s at
 * exactly 1000x1500. Keep the fixed-pixel stage and make every animation a
 * seamless CSS loop (periods divide 10s) that autostarts on load.
 */
const Top = () => {
  const [users, setUsers] = useState(
    Array.from({ length: 5 }, () => ({
      url: DEFAULT_AVATAR,
      username: "Loading...",
      level: null,
    }))
  );

  useEffect(() => {
    const fetchTopUsers = async () => {
      try {
        const topData = await getTop();
        if (!topData || topData.length === 0) {
          throw new Error("No top users data received");
        }

        const userPromises = topData.slice(0, 5).map(async (user, index) => {
          try {
            const userDetails = await getUsernameAndUrl(user.userid);
            return {
              url: userDetails.displayAvatarURL || DEFAULT_AVATAR,
              username: userDetails.tag || `User ${index + 1}`,
              level: user.level,
            };
          } catch (err) {
            console.error(`Error fetching user ${index + 1}:`, err);
            return {
              url: DEFAULT_AVATAR,
              username: `User ${index + 1}`,
              level: user.level,
            };
          }
        });

        const userDetails = await Promise.all(userPromises);
        setUsers(userDetails);
      } catch (err) {
        console.error("Error fetching top users:", err);
        // Keep default placeholder data on error
      }
    };

    fetchTopUsers();
  }, []);

  const medal = ["🥇", "🥈", "🥉", "", ""];
  const barWidths = ["100%", "82%", "68%", "54%", "44%"];
  const barColors = [
    "linear-gradient(90deg, #D53C35, #f59e0b)",
    "linear-gradient(90deg, #94a3b8, #e2e8f0)",
    "linear-gradient(90deg, #b45309, #f59e0b)",
    "linear-gradient(90deg, #7c3aed, #d946ef)",
    "linear-gradient(90deg, #0ea5e9, #7c3aed)",
  ];

  return (
    <div
      style={{
        width: "1000px",
        height: "1500px",
        overflow: "hidden",
        position: "relative",
        background:
          "radial-gradient(circle at 20% 0%, rgba(213,60,53,0.25), transparent 50%), radial-gradient(circle at 80% 100%, rgba(124,58,237,0.25), transparent 50%), #0a0a0f",
        fontFamily: "'Bebas Neue', sans-serif",
        color: "#fff",
      }}
    >
      {/* Looping CSS for the recording */}
      <style>{`
        @keyframes topShine {
          0% { transform: translateX(-150%) skewX(-20deg); }
          100% { transform: translateX(400%) skewX(-20deg); }
        }
        @keyframes topFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes topGlow {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 0.8; }
        }
      `}</style>

      {/* Header */}
      <div style={{ textAlign: "center", paddingTop: "60px", position: "relative" }}>
        <p style={{ fontSize: "26px", letterSpacing: "12px", color: "rgba(255,255,255,0.4)", margin: 0 }}>
          REO レオ GLOBAL
        </p>
        <h1
          style={{
            fontSize: "110px",
            margin: "0",
            lineHeight: 1,
            background: "linear-gradient(90deg, #D53C35, #fb7185, #7c3aed)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          LEADERBOARD
        </h1>
        <div
          style={{
            width: "280px",
            height: "3px",
            margin: "18px auto 0",
            background: "linear-gradient(90deg, transparent, #D53C35, transparent)",
            animation: "topGlow 2.5s ease-in-out infinite",
          }}
        />
      </div>

      {/* Rows */}
      <div style={{ padding: "50px 70px 0" }}>
        {users.map((user, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "34px",
              padding: "26px 34px",
              borderRadius: "24px",
              position: "relative",
              overflow: "hidden",
              background: "rgba(255,255,255,0.05)",
              border: i === 0 ? "1px solid rgba(213,60,53,0.5)" : "1px solid rgba(255,255,255,0.1)",
              animation: i < 3 ? `topFloat ${5 + i}s ease-in-out infinite` : "none",
            }}
          >
            {/* Shine sweep — loops every 5s */}
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                width: "120px",
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)",
                animation: `topShine 5s linear infinite`,
                animationDelay: `${i * 0.6}s`,
              }}
            />
            <h2 style={{ fontSize: "64px", width: "110px", margin: 0, color: i === 0 ? "#D53C35" : "rgba(255,255,255,0.85)" }}>
              #{i + 1}
            </h2>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <img
                src={user.url || DEFAULT_AVATAR}
                height="130"
                width="130"
                alt={`top${i + 1}`}
                style={{
                  borderRadius: "50%",
                  border: "4px solid rgba(255,255,255,0.2)",
                  display: "block",
                }}
              />
              {medal[i] && (
                <span style={{ position: "absolute", bottom: "-8px", right: "-8px", fontSize: "44px" }}>
                  {medal[i]}
                </span>
              )}
            </div>
            <div style={{ flexGrow: 1, marginLeft: "34px", minWidth: 0 }}>
              <h1
                style={{
                  fontSize: "46px",
                  margin: "0 0 14px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user.username}
              </h1>
              <div
                style={{
                  height: "14px",
                  borderRadius: "8px",
                  background: "rgba(255,255,255,0.08)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: barWidths[i],
                    borderRadius: "8px",
                    background: barColors[i],
                  }}
                />
              </div>
            </div>
            {user.level != null && (
              <div style={{ marginLeft: "30px", textAlign: "center", flexShrink: 0 }}>
                <p style={{ fontSize: "20px", letterSpacing: "4px", color: "rgba(255,255,255,0.4)", margin: 0 }}>LVL</p>
                <p style={{ fontSize: "52px", margin: 0 }}>{user.level}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div
        style={{
          position: "absolute",
          bottom: "50px",
          left: 0,
          right: 0,
          textAlign: "center",
        }}
      >
        <h1 style={{ fontSize: "42px", margin: 0, color: "rgba(255,255,255,0.9)" }}>
          SEASON 2 · KAMINARI COOPERATION
        </h1>
        <p style={{ fontSize: "22px", letterSpacing: "6px", color: "rgba(255,255,255,0.35)", margin: "8px 0 0" }}>
          KEEP CHATTING · KEEP CLIMBING
        </p>
      </div>
    </div>
  );
};

export default Top;
